import { useEffect, useState } from 'react'
import { communicationService } from '../services/communication'
import type { Lead, WhatsAppTemplate } from '../../../types'
import { MessageSquare, Send, ChevronDown, ChevronUp, X, AlertCircle } from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'

interface WhatsAppSendModalProps {
  isOpen: boolean
  lead: Lead
  onClose: () => void
  onSent: () => void
}

/** Replaces {{variable_name}} placeholders in a template body with provided values. */
function interpolate(body: string, vars: Record<string, string>): string {
  return body.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`)
}

/** Extract {{variable_name}} tokens from a template body string. */
function extractVars(body: string): string[] {
  const matches = body.match(/\{\{(\w+)\}\}/g) ?? []
  return [...new Set(matches.map(m => m.slice(2, -2)))]
}

// Variables the backend auto-injects from channel config — hide from the form.
const SERVER_FILLED = new Set(['dealer_name', 'dealer_phone'])

/** Build a prefill map from lead fields for known variable names. */
function leadVars(lead: Lead): Record<string, string> {
  return {
    customer_name:      lead.customer_name ?? '',
    vehicle_no:         lead.vehicle_reg_no ?? '',
    vehicle_reg:        lead.vehicle_reg_no ?? '',
    vehicle_reg_no:     lead.vehicle_reg_no ?? '',
    policy_number:      lead.policy_number ?? '',
    date:               lead.insurance_due_date
      ? new Date(lead.insurance_due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : '',
    insurance_due_date: lead.insurance_due_date
      ? new Date(lead.insurance_due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '',
  }
}

export default function WhatsAppSendModal({
  isOpen,
  lead,
  onClose,
  onSent,
}: WhatsAppSendModalProps) {
  const [mode, setMode]                         = useState<'template' | 'freeform'>('template')
  const [templates, setTemplates]               = useState<WhatsAppTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [selected, setSelected]                 = useState<WhatsAppTemplate | null>(null)
  const [variables, setVariables]               = useState<Record<string, string>>({})
  const [showPreview, setShowPreview]           = useState(false)
  const [freeformMessage, setFreeformMessage]   = useState('')
  const [sending, setSending]                   = useState(false)
  const [error, setError]                       = useState('')

  const prefill = leadVars(lead)

  useEffect(() => {
    if (!isOpen) return
    setMode('template')
    setSelected(null)
    setVariables({})
    setShowPreview(false)
    setFreeformMessage('')
    setError('')
    setLoadingTemplates(true)
    communicationService
      .listTemplates()
      .then((ts: WhatsAppTemplate[]) =>
        setTemplates(ts.filter((t: WhatsAppTemplate) => t.is_active && t.approval_status === 'approved'))
      )
      .catch(() => setError('Could not load templates'))
      .finally(() => setLoadingTemplates(false))
  }, [isOpen])

  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setSelected(template)
    const vars: Record<string, string> = {}
    extractVars(template.body ?? '').forEach(v => {
      if (!SERVER_FILLED.has(v)) {
        vars[v] = prefill[v] ?? ''
      }
    })
    setVariables(vars)
    setShowPreview(false)
  }

  const handleSend = async () => {
    if (mode === 'template' && !selected) return
    if (mode === 'freeform' && !freeformMessage.trim()) return
    setError('')
    setSending(true)
    try {
      if (mode === 'template') {
        await communicationService.sendWhatsApp(lead.id, selected!.name, variables)
      } else {
        await communicationService.sendWhatsAppText(lead.id, freeformMessage.trim())
      }
      onSent()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  if (!isOpen) return null

  const varKeys       = selected ? extractVars(selected.body ?? '').filter(k => !SERVER_FILLED.has(k)) : []
  const allVars       = selected ? { ...prefill, ...variables } : {}
  const previewBody   = selected ? interpolate(selected.body ?? '', allVars) : ''
  const allVarsFilled = varKeys.every(k => !!variables[k])

  return (
    <ModalOverlay onClose={onClose}>
      <div
        className="modal-panel flex flex-col"
        style={{ maxWidth: 520, maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center gap-2.5 min-w-0">
            <MessageSquare className="h-5 w-5 shrink-0" style={{ color: 'var(--success-text)' }} />
            <div className="min-w-0">
              <h2 className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                Send WhatsApp — {lead.customer_name}
              </h2>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {mode === 'template' ? 'Select an approved template to send' : 'Send a free-form message'}
              </p>
            </div>
          </div>
          <button className="btn-icon shrink-0" onClick={onClose} disabled={sending}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 px-5 pt-4">
          <button
            className={mode === 'template' ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: 12, height: 30, padding: '0 12px' }}
            onClick={() => setMode('template')}
            disabled={sending}
          >
            Template
          </button>
          <button
            className={mode === 'freeform' ? 'btn-primary' : 'btn-secondary'}
            style={{ fontSize: 12, height: 30, padding: '0 12px' }}
            onClick={() => setMode('freeform')}
            disabled={sending}
          >
            Free-form Message
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="alert-danger">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mode === 'freeform' ? (
            <div className="space-y-3">
              <div className="alert-warning">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  This will only reach the customer if they messaged within the last 24 hours
                  (WhatsApp&#x2019;s customer service window). Outside that window Meta rejects it —
                  use a template instead. If it fails, check Message History below for the reason.
                </span>
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="input"
                  rows={5}
                  maxLength={4096}
                  value={freeformMessage}
                  onChange={e => setFreeformMessage(e.target.value)}
                  placeholder="Type your message…"
                  autoFocus
                />
                <p className="form-hint">{freeformMessage.length}/4096</p>
              </div>
            </div>
          ) : (
            <>
              {/* Template list */}
              <div className="form-group">
                <label className="form-label">Template</label>
                {loadingTemplates ? (
                  <div className="empty-state py-6">
                    <div className="spinner" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="empty-state py-6">
                    <MessageSquare className="h-8 w-8 mb-2" style={{ color: 'var(--text-disabled)' }} />
                    <p>No approved templates available</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {templates.map(t => (
                      <button
                        key={t.id}
                        onClick={() => handleSelectTemplate(t)}
                        className="w-full text-left px-3 py-2.5 rounded-md border transition-colors"
                        style={selected?.id === t.id ? {
                          borderColor: 'var(--brand-primary)',
                          background: 'var(--brand-primary-subtle)',
                        } : {
                          borderColor: 'var(--border-default)',
                          background: 'transparent',
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {t.name}
                          </span>
                          <span className={t.category === 'utility' ? 'badge-info' : 'badge-purple'}>
                            {t.category}
                          </span>
                        </div>
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                          {t.body}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Variable inputs — only show fields not prefilled or server-filled */}
              {selected && varKeys.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Template variables
                  </p>
                  {varKeys.map(k => (
                    <div key={k} className="form-group">
                      <label className="form-label font-mono">{`{{${k}}}`}</label>
                      <input
                        type="text"
                        className="input text-sm"
                        value={variables[k] ?? ''}
                        onChange={e => setVariables(prev => ({ ...prev, [k]: e.target.value }))}
                        placeholder={`Value for ${k}`}
                      />
                      {prefill[k] && variables[k] === prefill[k] && (
                        <p className="form-hint">Auto-filled from lead</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Preview toggle */}
              {selected && (
                <div>
                  <button
                    className="flex items-center gap-1 text-sm transition-colors"
                    style={{ color: 'var(--text-tertiary)' }}
                    onClick={() => setShowPreview(v => !v)}
                  >
                    {showPreview ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showPreview ? 'Hide preview' : 'Show preview'}
                  </button>
                  {showPreview && (
                    <div
                      className="mt-2 p-3 rounded-md"
                      style={{
                        background: 'var(--surface-subtle)',
                        border: '1px solid var(--border-default)',
                      }}
                    >
                      {selected.header && (
                        <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                          {selected.header}
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                        {previewBody}
                      </p>
                      {selected.footer && (
                        <p className="text-xs mt-1 italic" style={{ color: 'var(--text-tertiary)' }}>
                          {selected.footer}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-3 px-5 py-4 shrink-0"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          <button className="btn-secondary" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="btn-primary flex items-center gap-2"
            onClick={handleSend}
            disabled={(mode === 'template' ? !selected || !allVarsFilled : !freeformMessage.trim()) || sending}
          >
            <Send className="h-4 w-4" />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}
